import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { fetchShortlist, type ShortlistItem } from '@/api/queries';
import ScoreBadge from '@/components/ScoreBadge';

export default function ShortlistScreen(): JSX.Element {
  const { data, isLoading, isError, refetch, error, isRefetching } = useQuery<ShortlistItem[]>({
    queryKey: ['shortlist'],
    queryFn: fetchShortlist,
  });

  return (
    <div className="flex flex-col gap-3 p-4">
      <header className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Потребує перегляду</h1>
        <button
          type="button"
          onClick={() => refetch()}
          className="rounded-md px-3 py-1 text-sm text-tg-link"
          disabled={isRefetching}
        >
          {isRefetching ? '…' : 'Оновити'}
        </button>
      </header>

      {isLoading && <div className="py-12 text-center text-tg-hint">Завантаження…</div>}
      {isError && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
          Не вдалося завантажити шортлист: {(error as Error).message}
        </div>
      )}
      {data && data.length === 0 && (
        <div className="rounded-md bg-tg-secondary p-6 text-center text-sm text-tg-hint">
          Це ваш шортліст: сюди автоматично потрапляють кандидати, яких AI відібрав за вашими
          вакансіями після скрин-оцінки (бал вище порогу). Поки нічого — нові кандидати
          зʼявляться після наступного AI-скрину.
        </div>
      )}
      <ul className="flex flex-col gap-2">
        {data?.map((item) => (
          <li key={item.match_score_id}>
            <Link
              to={`/candidate/${item.candidate.id}?vacancy=${item.vacancy.id}`}
              className="block rounded-lg border border-tg-secondary bg-tg-bg p-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-tg-text">
                    {item.candidate.full_name}
                  </div>
                  <div className="truncate text-xs text-tg-hint">{item.vacancy.title}</div>
                </div>
                <ScoreBadge
                  score={item.score}
                  scaleMax={item.scale_max}
                  category={item.category}
                />
              </div>
              <div className="mt-2 flex items-center gap-2 text-xs text-tg-hint">
                <span className="rounded bg-tg-secondary px-2 py-0.5">
                  {item.candidate.source}
                </span>
                {item.tag === 're_suggestion' && (
                  <span className="rounded bg-tg-secondary px-2 py-0.5">
                    ♻️ повторна
                  </span>
                )}
                {/* Cold-search hits reach this inbox too now. Without a marker
                    they are indistinguishable from someone who applied — and
                    those are two different conversations to open with. */}
                {item.tag === 'cold_search' && (
                  <span className="rounded bg-tg-secondary px-2 py-0.5">
                    🔍 холодний пошук
                  </span>
                )}
                <span className="ml-auto">{formatRelative(item.created_at)}</span>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function formatRelative(iso: string): string {
  const ts = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - ts);
  const minutes = Math.round(diff / 60_000);
  if (minutes < 1) return 'щойно';
  if (minutes < 60) return `${minutes} хв тому`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} год тому`;
  return new Date(iso).toLocaleDateString('uk-UA');
}
